import { PayloadAction } from '@reduxjs/toolkit';
import { QuoteSaveSource } from '@snap-types/models/enums';
import { buildIssueId } from '@ui/features/quote-builder/state/utilities/issue-id';
import {
  deleteIssue,
  upsertIssue,
} from '@ui/features/quote-builder/state/utilities/issue-managers';
import { setRawFieldValues } from '@ui/features/quote-builder/state/utilities/raw-field-values';
import { stringifyQuote } from '@ui/features/quote-builder/state/utilities/stringify-quote';
import { QuoteBuilderStateType } from '@ui/features/quote-builder/types/state';
import { WritableDraft } from 'immer';

import { quoteBuilderFieldValidators } from '../fields';

const quoteRejectedIssueId = buildIssueId({
  area: 'quote',
  type: 'rejected',
});

export function quoteUpdateTriggered(
  state: WritableDraft<QuoteBuilderStateType>,
) {
  deleteIssue(state, quoteRejectedIssueId);
  state.isUpdating = true;
}

export function quoteUpdateSucceeded(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<{ source: QuoteSaveSource }>,
) {
  state.isUpdating = false;
  state.lastSaveDateTime = new Date();
  state.currentServerData = stringifyQuote(state);

  /**
   * @NOTE(shawk): When the quote is auto-saved or saved by the system, we do
   * not want to clear out the raw field values since a user may be in the
   * middle of editing a field. Only clear raw field values on manual saves.
   */
  if (action.payload.source !== QuoteSaveSource.MANUAL) {
    return;
  }

  const rawFieldEntries = Object.keys(quoteBuilderFieldValidators).map(
    (field) => {
      const value = state[field as keyof QuoteBuilderStateType];

      return {
        field,
        uuid: state.uuid,
        value: value === null || value === undefined ? '' : value.toString(),
      };
    },
  );

  setRawFieldValues(state, rawFieldEntries);
}

export function quoteUpdateFailed(state: WritableDraft<QuoteBuilderStateType>) {
  state.isUpdating = false;

  upsertIssue(state, quoteRejectedIssueId, {
    title: 'Failed to Update Quote',
    description: 'Quote could not be updated in the database',
    area: 'quote',
    severity: 'error',
    type: 'rejected',
    action: {
      type: 'unrecoverable',
    },
  });
}
